/**
 * Idempotent upsert of a ``taskSummary`` aria message into the
 * Workspace messages array (harmoniq-558).
 *
 * The bug this fixes
 * ──────────────────
 * Before this helper, the FE had two code paths that both APPENDED
 * a taskSummary entry for the SAME server-side event, with no
 * dedup:
 *
 *   • ``parseTaskHistory`` (on /task/restart rebuild — walks the
 *     durable ``conversation_log``)
 *   • Workspace ``onMessage`` handler for ``{type:"question",
 *     content.type:"task_summary"}`` (live WS events)
 *
 * When a user opened a task that was paused at task_summary HITL,
 * both paths fired back-to-back:
 *
 *   1. /task/restart rebuilt ``messages`` from conversation_log
 *      → task_summary #1 appended.
 *   2. WebSocket connected, cached question/task_summary event
 *      replayed → task_summary #2 appended.
 *
 * Observed on task_6f3fb9dc (2026-04-22): the UI showed two
 * identical "Product Sales by Week – 2025" cards stacked.
 *
 * The design fix
 * ──────────────
 * Model taskSummary as a singleton: at any point in the current
 * turn there is EXACTLY ONE task_summary that the user needs to
 * respond to. A second arrival must REPLACE the existing one
 * in-place, not be appended. Matches the already-correct progress-
 * updates pattern (``accumulateProgressSteps`` upserts by step
 * label instead of appending).
 *
 * Dedup key: we look at the LAST aria message in the array. If
 * it's already a taskSummary (regardless of task_name — two
 * taskSummaries back-to-back within one turn is always a dup,
 * since the agent can only be paused at one HITL at a time), we
 * replace in place. Otherwise we append.
 *
 * Why "last aria" rather than "any aria": after a task_summary
 * pauses, the next events are progress/result cards on resume.
 * Those cards push the taskSummary further back in the array.
 * If a FUTURE turn somehow re-emits another task_summary, it
 * legitimately belongs as a NEW entry at the tail — the prior
 * one is a historical artifact of the earlier turn. The "last
 * aria is already a taskSummary" check captures "these two
 * arrivals are for the same pause" without cross-turn false
 * merging.
 */

/**
 * The chat renders a local-time clock label from ``msg.ts``, but a
 * taskSummary message is built here rather than via ``messageAppended``
 * (which stamps ``ts`` itself), so it would otherwise carry none and the
 * card would show no time. ``question_at`` is that very instant — the WS
 * frame's timestamp (useWorkspaceWebSocket passes ``question_at: ts``)
 * and, on replay, parseTaskHistory's wire timestamp — so mirror it onto
 * the message. ``fallback`` keeps an already-captured ts when a replay
 * arrives without ``question_at``. Returns a spreadable object so a
 * missing ts stays absent instead of becoming an explicit undefined.
 */
function tsFrom(summary, fallback) {
  const ts = summary?.question_at ?? fallback;
  return ts == null ? {} : { ts };
}

/**
 * Return a new messages array with ``summary`` upserted as a
 * taskSummary aria entry. Pure function — does NOT mutate
 * ``messages``.
 *
 * @param {Array} messages — current messages array
 * @param {object} summary — the taskSummary object (intro,
 *   task_name, rows, steps, action) — whatever shape the caller
 *   wants stored. The helper doesn't introspect the contents.
 * @returns {Array} — new messages array
 */
export function upsertTaskSummary(messages, summary) {
  if (!Array.isArray(messages))
    return [{ role: "aria", taskSummary: summary, ...tsFrom(summary) }];
  // Find the most recent aria entry. If it's a taskSummary, replace
  // it in place. Otherwise append.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || m.role !== "aria") continue;
    if (m.taskSummary) {
      // Replace in place, preserving array order so any non-aria
      // entries after this (user bubbles, etc.) keep their
      // position.
      //
      // Timing preservation (2026-04-22, step-timings feature): the
      // replay + live race for a SINGLE same-pause event may emit
      // the taskSummary twice. The second arrival typically has
      // identical content but may be missing timing fields we
      // already captured:
      //   • `question_at` — set by parseTaskHistory from the wire
      //     `content.timestamp`; may be absent if the live handler
      //     constructs the event object without copying it.
      //   • `answered_at` — set by the live Start-Run click handler
      //     AFTER the summary was rendered; the WS-replay form of
      //     the event definitely doesn't have it.
      // If the new summary lacks either field and the existing one
      // has it, we keep the existing value so a replay doesn't
      // erase state the live path captured. If the new summary
      // does supply the field, the new value wins (agents can
      // update — e.g. a revised plan re-asserting `question_at`).
      const existing = m.taskSummary || {};
      // Detect a genuinely-new question (agent re-asked after the user
      // clicked Make changes) vs. a replay/dedup of the same one. A
      // re-asked plan changes ``task_name`` / ``intro`` / rows, or
      // arrives with a later ``question_at`` than what we have stored.
      // Replays carry the same content and (when set) the same
      // ``question_at``. When it's a new question we must drop the
      // prior turn's ``answered_at`` + ``outcome`` so the CTAs are
      // re-enabled — otherwise the card renders answered but with no
      // outcome and the action row is hidden ([TaskSummaryCard.jsx]
      // visibility check ``!isAnswered || outcome === "changes"``).
      const isNewQuestion =
        (summary?.question_at != null &&
          existing.question_at != null &&
          summary.question_at !== existing.question_at) ||
        (summary?.task_name != null &&
          existing.task_name != null &&
          summary.task_name !== existing.task_name) ||
        (summary?.intro != null &&
          existing.intro != null &&
          summary.intro !== existing.intro);
      const merged = {
        ...summary,
        question_at: summary?.question_at ?? existing.question_at,
        answered_at: isNewQuestion
          ? null
          : (summary?.answered_at ?? existing.answered_at),
        outcome: isNewQuestion ? null : (summary?.outcome ?? existing.outcome),
      };
      if (isNewQuestion) {
        // Re-asked plan after Make changes: drop the stale card from
        // its original slot and append at the tail. Otherwise the
        // user bubble the user just sent ("Also duplicate the file")
        // sits AFTER the card in the message array, lastUserIdx >
        // lastAriaIdx, and the parent's ``lastActiveIdx`` calc
        // (Workspace.jsx) returns -1 — keeping ``actionsEnabled``
        // false on the new question's CTAs even though they should
        // be live.
        const next = messages.slice();
        next.splice(i, 1);
        next.push({ ...m, taskSummary: merged, ...tsFrom(merged, m.ts) });
        return next;
      }
      const next = messages.slice();
      next[i] = { ...m, taskSummary: merged, ...tsFrom(merged, m.ts) };
      return next;
    }
    // Last aria is something else (progressCard, resultCard, etc.).
    // A new taskSummary genuinely belongs at the tail.
    break;
  }
  return [
    ...messages,
    { role: "aria", taskSummary: summary, ...tsFrom(summary) },
  ];
}
